import React from 'react';
import _ from 'lodash';

import {
  ContainerBox,
  fields,
  Form,
  TestModeWarningBar
} from 'iron-forge-react-modules';
import {
  Box,
  Button,
  Drawer,
  Paper as MuiPaper,
  styled,
  Typography
} from '@mui/material';
import {
  activityTemplates,
  appFormUtil,
  CustomerOrderSummary,
  isProd,
  routeDirectory
} from 'modules';
import {
  useLocation,
  useNavigate
} from 'react-router-dom';

import { useForm } from 'react-hook-form';
import * as types from './types';
import * as dataService from './dataService';



const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();

const RootBox = styled(Box)(() => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)'
}));

const Paper = styled(MuiPaper)(({ theme }) => ({
  padding: theme.spacing(4)
}));

const DescriptionTypography = styled(Typography)(({ theme }) => ({
  maxWidth: 400,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2)
}));

const ErrorTypography = styled(DescriptionTypography)(({ theme }) => ({
  color: theme.palette.error.main,
  marginBottom: theme.spacing(3)
}));

const ButtonBox = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2),
  justifyContent: 'flex-end'
}));

const LogoBox = styled(Box)(() => ({
  height: 80,
  textAlign: 'center',

  '& img': {
    height: '100%'
  }
}));



export const OrderLookupActivity: React.FC = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const storeId = params.get('storeId') ?? '';
  const email = params.get('email') ?? '';
  const [fnQuery, tuple] = dataService.useQuery(storeId);
  const [isReset, setIsReset] = React.useState(false);
  const navigate = useNavigate();

  const form = useForm<types.IFormState>({
    defaultValues: {
      customerEmail: email || '',
      orderNumber: ''
    }
  });



  function processSubmit(form: types.IFormState) {
    fnQuery(appFormUtil.trimFormStateValues(form));
    setIsReset(false);
  }



  function handleClickStartOver() {
    setIsReset(true);
  }



  function handleClickClaimOrder() {
    if (tuple.data.order) {
      navigate(routeDirectory.customer.orderDetail.path(tuple.data.order.id, storeId));
    }
  }



  const orderNotFound = React.useMemo(() => tuple.called && !tuple.loading && _.isNil(tuple.data.order), [tuple]);

  return (
    <activityTemplates.CustomerActivityTemplate.FC storeId={storeId}>
      <ContainerBox.FC>
        {!isProd && (
          <TestModeWarningBar.FC />
        )}

        <RootBox>
          <Form.FC onSubmit={form.handleSubmit(processSubmit)}>
            <Paper>
              {tuple.data.logoUrl && (
                <LogoBox>
                  <img src={tuple.data.logoUrl} />
                </LogoBox>
              )}

              <Typography variant="h2">
                Order lookup
              </Typography>

              <DescriptionTypography>
                When you placed an order, you were provided with an order number. Enter that in addition to your email to find the order in our system.
              </DescriptionTypography>

              {orderNotFound && (
                <ErrorTypography>
                  Order {form.getValues('orderNumber')} was not found with email {form.getValues('customerEmail')}
                </ErrorTypography>
              )}

              <ControlledTextField
                isDisabled={tuple.loading}
                fieldKey="orderNumber"
                formControl={form.control}
                label="Order Number"
                extraValidationRules={{
                  minLength: 6
                }}
                isRequired />

              <ControlledTextField
                type="email"
                isDisabled={tuple.loading}
                fieldKey="customerEmail"
                formControl={form.control}
                label="Email"
                isRequired />

              <Button
                variant="contained"
                disabled={tuple.loading}
                type="submit">
                Find my order
              </Button>
            </Paper>
          </Form.FC>
        </RootBox>

      </ContainerBox.FC>

      <Drawer
        open={!tuple.loading && !_.isNil(tuple.data.order) && !isReset}
        anchor="right">
        {tuple.data.order && (
          <CustomerOrderSummary.FC
            order={tuple.data.order} />
        )}

        <ButtonBox>
          <Button onClick={handleClickStartOver}>
            Not you? Start over
          </Button>

          <Button
            variant="contained"
            onClick={handleClickClaimOrder}>
            Yes! This is my order
          </Button>
        </ButtonBox>
      </Drawer>
    </activityTemplates.CustomerActivityTemplate.FC>
  );
};
